import { useState, useEffect, useCallback, useRef } from 'react';
import type { DisplayData, DisplaySessionItem } from '@/types';
import { useSocket } from '@/hooks/useSocket';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

interface DisplayBoardProps {
  businessId: string;
}

export function DisplayBoard({ businessId }: DisplayBoardProps) {
  const [data, setData] = useState<DisplayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [newReadyIds, setNewReadyIds] = useState<Set<string>>(new Set());
  const prevReadyIds = useRef<Set<string>>(new Set());
  const { connected, lastEvent, joinDisplay } = useSocket();

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/display/${businessId}`);
      if (res.ok) {
        const fresh = (await res.json()) as DisplayData;
        const currentReadyIds = new Set(fresh.ready.map((s) => s.id));
        const newlyReady = new Set<string>();
        currentReadyIds.forEach((id) => {
          if (!prevReadyIds.current.has(id)) {
            newlyReady.add(id);
          }
        });
        if (newlyReady.size > 0) {
          setNewReadyIds(newlyReady);
          setTimeout(() => setNewReadyIds(new Set()), 5000);
          // Play sound if enabled
          if (fresh.business?.notification_sound !== false) {
            playNotificationSound();
          }
        }
        prevReadyIds.current = currentReadyIds;
        setData(fresh);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (connected && businessId) {
      joinDisplay(businessId);
    }
  }, [connected, businessId, joinDisplay]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === 'display:updated' || lastEvent.event === 'session:notified' || lastEvent.event === 'queue:changed') {
      fetchData();
    }
  }, [lastEvent, fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--qr-bg)', color: 'var(--qr-text)' }}>
        <div className="animate-pulse text-2xl" style={{ color: 'var(--qr-text-secondary)' }}>Cargando...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--qr-bg)', color: 'var(--qr-text)' }}>
        <p className="text-xl">No se pudo cargar la pantalla</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col min-h-screen overflow-hidden select-none"
      style={{ background: 'var(--qr-bg)', color: 'var(--qr-text)' }}
    >
      {/* Header */}
      <header className="flex flex-col items-center justify-center py-6 px-4" style={{ borderBottom: '2px solid var(--qr-border)' }}>
        {data.business.logo_url && (
          <img src={data.business.logo_url} alt="logo" className="h-16 object-contain mb-2" />
        )}
        <h1 className="text-center font-extrabold" style={{ fontSize: '48px', lineHeight: 1.1, color: 'var(--qr-text)' }}>
          {data.business.name}
        </h1>
      </header>

      {/* Ready section */}
      <section className="flex-1 flex flex-col px-6 py-6">
        <h2
          className="text-center font-extrabold uppercase tracking-widest mb-4"
          style={{ fontSize: '32px', color: 'var(--qr-success)' }}
        >
          Pedidos listos
        </h2>
        {data.ready.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {data.ready.map((s) => (
              <ReadyCard key={s.id} session={s} isNew={newReadyIds.has(s.id)} />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center flex-1">
            <p className="text-2xl" style={{ color: 'var(--qr-text-secondary)' }}>Esperando pedidos...</p>
          </div>
        )}
      </section>

      {/* Waiting section */}
      <section className="px-6 py-4" style={{ background: 'var(--qr-surface)', borderTop: '2px solid var(--qr-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-extrabold uppercase tracking-widest" style={{ fontSize: '24px', color: 'var(--qr-primary)' }}>
            En espera
          </h2>
          <span className="font-extrabold" style={{ fontSize: '48px', color: 'var(--qr-text)' }}>
            {data.stats.total_waiting}
          </span>
        </div>
        {data.waiting.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {data.waiting.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-3 px-4 py-2 rounded-lg"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)' }}
              >
                <span className="font-mono font-bold text-lg">{s.public_id}</span>
                <span className="text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
                  {s.client_name} · {s.party_size}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--qr-text-secondary)' }}>Nadie en espera</p>
        )}
      </section>

      {/* Stats bar */}
      <footer className="flex items-center justify-between px-6 py-3 text-sm" style={{ background: 'var(--qr-bg)', borderTop: '1px solid var(--qr-border)' }}>
        <div className="flex gap-6" style={{ color: 'var(--qr-text-secondary)' }}>
          <span>
            Tiempo medio hoy:{' '}
            <strong style={{ color: 'var(--qr-text)' }}>
              {Math.round(data.stats.avg_wait_today / 60)} min
            </strong>
          </span>
          <span>
            Atendidos:{' '}
            <strong style={{ color: 'var(--qr-text)' }}>{data.stats.total_served_today}</strong>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span style={{ color: 'var(--qr-text-secondary)' }}>{connected ? 'Conectado' : 'Sin conexión'}</span>
        </div>
      </footer>
    </div>
  );
}

function ReadyCard({ session, isNew }: { session: DisplaySessionItem; isNew: boolean }) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-5 rounded-xl font-extrabold transition-transform duration-500 ${
        isNew ? 'animate-scale-in' : ''
      }`}
      style={{
        fontSize: '64px',
        background: 'var(--qr-success)',
        color: '#fff',
        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
      }}
    >
      <span className="font-mono">{session.public_id}</span>
      {session.table_number && (
        <span className="text-lg font-semibold mt-1">Mesa {session.table_number}</span>
      )}
    </div>
  );
}

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    // ignore audio errors
  }
}
